/**
 * Secure Token Display Component
 * Provides masked token display with secure copy functionality
 */

import { AlertTriangle, Copy, Eye, EyeOff, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";

interface SecureTokenDisplayProps {
	token: string;
	onClose?: () => void;
	title?: string;
	description?: string;
	className?: string;
}

export function SecureTokenDisplay({
	token,
	onClose,
	title = "Token Created Successfully",
	description = "Make sure to copy your token now. You will not be able to see it again!",
	className,
}: SecureTokenDisplayProps) {
	const [isVisible, setIsVisible] = useState(false);
	const [copied, setCopied] = useState(false);
	const [copyCount, setCopyCount] = useState(0);
	const [timeLeft, setTimeLeft] = useState(300); // 5 minutes auto-close

	// Auto-close timer
	useEffect(() => {
		if (timeLeft <= 0) {
			onClose?.();
			return;
		}

		const timer = setTimeout(() => {
			setTimeLeft((prev) => prev - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [timeLeft, onClose]);

	// Reset copied state after 3 seconds
	useEffect(() => {
		if (copied) {
			const timer = setTimeout(() => setCopied(false), 3000);
			return () => clearTimeout(timer);
		}
	}, [copied]);

	const handleCopyToken = async () => {
		try {
			// Use modern clipboard API with fallback
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(token);
			} else {
				// Fallback for older browsers or non-HTTPS
				const textArea = document.createElement("textarea");
				textArea.value = token;
				textArea.style.position = "fixed";
				textArea.style.opacity = "0";
				document.body.appendChild(textArea);
				textArea.focus();
				textArea.select();
				try {
					document.execCommand("copy");
				} finally {
					document.body.removeChild(textArea);
				}
			}

			setCopied(true);
			setCopyCount((prev) => prev + 1);
		} catch (error) {
			console.error("Failed to copy token:", error);
			// Show error to user
			alert("Failed to copy token. Please select and copy manually.");
		}
	};

	const maskedToken = token.replace(
		/(.{8})(.*)(.{8})/,
		"$1••••••••••••••••••••$3",
	);

	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	return (
		<div className={cn("space-y-4 p-4 border rounded-lg bg-white", className)}>
			{/* Header */}
			<div className="space-y-2">
				<div className="flex items-center gap-2">
					<Shield className="w-5 h-5 text-blue-600" />
					<h3 className="text-lg font-semibold">{title}</h3>
					<div className="ml-auto text-sm text-orange-600 font-medium">
						Auto-close: {formatTime(timeLeft)}
					</div>
				</div>
				<p className="text-sm text-gray-600">{description}</p>
			</div>

			{/* Security Warning */}
			<div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
				<AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
				<div className="space-y-1">
					<p className="text-sm font-medium text-yellow-800">Security Notice</p>
					<ul className="text-xs text-yellow-700 space-y-1">
						<li>• This token will not be shown again after closing</li>
						<li>• Store it securely (password manager recommended)</li>
						<li>• Treat it like a password - never share it</li>
						<li>• Revoke immediately if compromised</li>
					</ul>
				</div>
			</div>

			{/* Token Display */}
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label className="text-sm font-medium">Your new token</Label>
					<div className="flex items-center gap-2 text-xs text-gray-500">
						<span>
							Copied {copyCount} time{copyCount !== 1 ? "s" : ""}
						</span>
						{copyCount > 3 && (
							<span className="text-orange-600 font-medium">
								• Consider security
							</span>
						)}
					</div>
				</div>

				<div className="flex items-center gap-2">
					<div className="relative flex-1">
						<Input
							readOnly
							value={isVisible ? token : maskedToken}
							className={cn(
								"font-mono text-sm pr-10",
								isVisible ? "text-gray-900" : "text-gray-500",
							)}
							style={{
								userSelect: isVisible ? "text" : "none",
								WebkitUserSelect: isVisible ? "text" : "none",
							}}
						/>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
							onClick={() => setIsVisible(!isVisible)}
						>
							{isVisible ? (
								<EyeOff className="w-4 h-4" />
							) : (
								<Eye className="w-4 h-4" />
							)}
						</Button>
					</div>

					<Button
						type="button"
						variant={copied ? "default" : "outline"}
						size="sm"
						onClick={handleCopyToken}
						className={cn(
							"min-w-[80px] transition-all duration-200",
							copied && "bg-green-600 hover:bg-green-700 text-white",
						)}
					>
						{copied ? (
							<>
								<Copy className="w-4 h-4 mr-2" />
								Copied!
							</>
						) : (
							<>
								<Copy className="w-4 h-4 mr-2" />
								Copy
							</>
						)}
					</Button>
				</div>
			</div>

			{/* Copy Instructions */}
			{!copied && (
				<div className="text-xs text-gray-500 space-y-1">
					<p>
						<strong>Tip:</strong> Click the eye icon to reveal the full token
						for manual copying
					</p>
					<p>
						<strong>Security:</strong> Token is automatically hidden to prevent
						shoulder surfing
					</p>
				</div>
			)}

			{/* Actions */}
			<div className="flex justify-between items-center pt-2 border-t">
				<div className="text-xs text-gray-500">
					Token expires in {formatTime(timeLeft)} or when closed
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setTimeLeft(timeLeft + 60)}
						className="text-xs"
					>
						+1 min
					</Button>
					<Button variant="default" size="sm" onClick={onClose}>
						I've stored it safely
					</Button>
				</div>
			</div>
		</div>
	);
}

/**
 * Hook for secure token display
 */
export function useSecureTokenDisplay() {
	const [tokenData, setTokenData] = useState<{
		token: string;
		timestamp: number;
	} | null>(null);

	const showToken = (token: string) => {
		setTokenData({
			token,
			timestamp: Date.now(),
		});
	};

	const hideToken = () => {
		setTokenData(null);
	};

	return {
		tokenData,
		showToken,
		hideToken,
		isVisible: !!tokenData,
	};
}
